import React from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { monitoring } from '@/lib/monitoring'
import { initSentry } from '@/lib/sentry'

// Initialize Sentry as early as possible
initSentry();

// ErrorBoundary component for catching React component/runtime errors
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Report to production error monitoring
    if (import.meta.env.PROD) {
      monitoring.captureError(error, {
        componentStack: errorInfo.componentStack,
        context: 'ErrorBoundary main.tsx',
      });
    } else {
      // Log the error and errorInfo for debugging in dev
      console.error('ErrorBoundary caught an error:', error);
      console.error('Error info:', errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      // Fallback UI when an error occurs
      return (
        <div 
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            textAlign: 'center',
            padding: '20px'
          }}>
          <h1 style={{ color: '#dc2626', marginBottom: '16px' }}>Something went wrong</h1>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>
            We encountered an error while running the application. Please check the console for more details.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Add error handling for the main app initialization
let root: ReturnType<typeof createRoot> | null = null;

try {
  const rootElement = document.getElementById("root");

  if (!rootElement) {
    throw new Error("Root element not found. Make sure there's a <div id='root'></div> in your HTML.");
  }

  root = createRoot(rootElement);
  root.render(
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
} catch (error) {
  // Report to production error monitoring
  if (import.meta.env.PROD) {
    monitoring.captureError(error instanceof Error ? error : new Error(String(error)), {
      context: 'App initialization',
      stage: 'render'
    });
  } else {
    console.error('Failed to render app:', error);
  }
  
  const rootElement = document.getElementById("root");
  
  // Clean up if root was created but render failed
  if (root) {
    root.unmount();
  }
  
  // Show a user-friendly error message
  if (rootElement) {
    rootElement.innerHTML = `
    <div role="alert" style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      text-align: center;
      padding: 20px;
    ">
      <h1 style="color: #dc2626; margin-bottom: 16px;">Something went wrong</h1>
      <p style="color: #6b7280; margin-bottom: 24px;">
        We encountered an error while loading the application. Please check the console for more details.
      </p>
      <button 
        onclick="window.location.reload()" 
        style="
          background: #3b82f6;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 16px;
        "
      >
        Reload Page
      </button>
    </div>
  `;
  }
}
